import React, { FC, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import {
    checkCustomerEInvoicingTemplate,
    customerEInvoicingTemplate,
    sendCustomerEInvoiceInviteTemplate,
} from 'apollo/templates/e-invoicing';
import { CustomerEInvoicing, CustomerEInvoicingStatus } from 'models/e-invoicing';
import { formatDate } from 'utils/format/format-date';
import EInvoiceInviteDialog from './e-invoice-invite-dialog.component';

type CustomerEInvoicingPanelProps = {
    clientId: string;
    /** Whether the customer's delivery method is e-invoice (chosen or enforced by the clinic). */
    isElectronicDelivery: boolean;
    /** Bumped each time the lab chooses e-invoice: the customer is then checked in Crediflow. */
    checkRequest: number;
    /** Lab admins may send invites. */
    canInvite: boolean;
    defaultEmail?: string;
    /** Crediflow's answer after a check, so the caller can keep or undo the choice. */
    onChecked: (status: CustomerEInvoicingStatus) => void;
    onCheckingChange: (isChecking: boolean) => void;
};

const SEVERITY: Record<CustomerEInvoicingStatus, 'success' | 'info' | 'warning'> = {
    [CustomerEInvoicingStatus.READY]: 'success',
    [CustomerEInvoicingStatus.INVITE_NEEDED]: 'info',
    [CustomerEInvoicingStatus.NOT_FOUND]: 'info',
    [CustomerEInvoicingStatus.DECLINED]: 'warning',
    [CustomerEInvoicingStatus.SEVERAL_MATCHES]: 'warning',
};

const canBeInvited = (status?: CustomerEInvoicingStatus) =>
    status === CustomerEInvoicingStatus.INVITE_NEEDED || status === CustomerEInvoicingStatus.NOT_FOUND;

/**
 * DD-3726: one line on whether this customer accepts e-invoices from the lab, and at most one
 * action: inviting the customer (lab admins). Shown while the delivery method is e-invoice, and
 * after the lab tried to choose it for a customer that has not accepted (or has an open invite).
 */
const CustomerEInvoicingPanel: FC<CustomerEInvoicingPanelProps> = ({
    clientId,
    isElectronicDelivery,
    checkRequest,
    canInvite,
    defaultEmail,
    onChecked,
    onCheckingChange,
}) => {
    const { t: translate } = useTranslation(['client-directory']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [eInvoicing, setEInvoicing] = useState<CustomerEInvoicing | null>(null);
    const [checkedNow, setCheckedNow] = useState(false);
    const [isInviteOpen, setIsInviteOpen] = useState(false);

    useQuery<{ customerEInvoicing: CustomerEInvoicing | null }>(customerEInvoicingTemplate, {
        variables: { clientId },
        fetchPolicy: 'network-only',
        onCompleted: (data) => setEInvoicing((current) => current ?? data.customerEInvoicing),
    });
    const [check, { loading: checking }] = useMutation(checkCustomerEInvoicingTemplate, {
        variables: { clientId },
        onCompleted: (data) => {
            setEInvoicing(data.checkCustomerEInvoicing);
            setCheckedNow(true);
            onChecked(data.checkCustomerEInvoicing.status);
        },
        onError: showBoundary,
    });
    const [sendInvite, { loading: inviting }] = useMutation(sendCustomerEInvoiceInviteTemplate, {
        onCompleted: (data) => {
            setEInvoicing(data.sendCustomerEInvoiceInvite);
            setIsInviteOpen(false);
        },
        onError: (error) => {
            setIsInviteOpen(false);
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (checkRequest > 0) {
            check();
        }
    }, [checkRequest]);

    useEffect(() => {
        onCheckingChange(checking);
    }, [checking]);

    if (checking) {
        return (
            <Box display="flex" alignItems="center" gap={1} mb={3} data-test="customer-e-invoicing.checking">
                <CircularProgress size={16} />
                <Typography variant="body2">{translate('client-directory:E_INVOICING_CHECKING')}</Typography>
            </Box>
        );
    }

    const status = eInvoicing?.status;
    const invite = eInvoicing?.invite;
    const isWaitingForCustomer = !!invite && canBeInvited(status);
    const isVisible =
        !!status &&
        (isElectronicDelivery || (status !== CustomerEInvoicingStatus.READY && (checkedNow || isWaitingForCustomer)));
    if (!isVisible) {
        return null;
    }

    const message = isWaitingForCustomer
        ? translate('client-directory:E_INVOICING_INVITE_SENT', {
              email: invite!.email,
              date: formatDate(invite!.sentAt, 'date'),
              interpolation: { escapeValue: false },
          })
        : translate(`client-directory:E_INVOICING_CUSTOMER_${status}`);
    const showInviteButton = canBeInvited(status) && !invite && canInvite;
    const showAskAdmin = canBeInvited(status) && !invite && !canInvite;

    return (
        <Box mb={3} data-test="customer-e-invoicing">
            <Alert
                severity={SEVERITY[status!]}
                data-test={`customer-e-invoicing.${status}`}
                action={
                    showInviteButton ? (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => setIsInviteOpen(true)}
                            disabled={inviting}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            data-test="customer-e-invoicing.open-invite"
                        >
                            {translate('client-directory:E_INVOICING_INVITE_CUSTOMER')}
                        </Button>
                    ) : undefined
                }
            >
                {message}
                {showAskAdmin ? ` ${translate('client-directory:E_INVOICING_INVITE_ADMIN_ONLY')}` : ''}
            </Alert>
            <EInvoiceInviteDialog
                open={isInviteOpen}
                defaultEmail={defaultEmail}
                sending={inviting}
                onClose={() => setIsInviteOpen(false)}
                onSend={(email, message) => sendInvite({ variables: { clientId, email, message } })}
            />
        </Box>
    );
};

export default CustomerEInvoicingPanel;
